import { useEffect, useState } from "react";
import { api, formatNaira } from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { Chip, priorityChip, statusChip } from "@/components/Chip";
import {
  Users, Target, Ticket as TicketIcon, FileText, AlertCircle, TrendingUp, Landmark
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { formatDate } from "@/lib/api";

const KPI = ({ label, value, icon: Icon, tone = "aqua", hint }) => (
  <div className="kpi-card" data-testid={`kpi-${label.toLowerCase().replace(/\s+/g, "-")}`}>
    <div className="flex items-center justify-between">
      <div className="text-xs uppercase tracking-wider text-slate-500 font-medium">{label}</div>
      <div className={`w-8 h-8 rounded-md flex items-center justify-center ${tone === "aqua" ? "bg-brand-aqua-light text-brand-aqua-hover" : tone === "danger" ? "bg-red-50 text-red-600" : "bg-slate-100 text-slate-600"}`}>
        <Icon className="w-4 h-4" />
      </div>
    </div>
    <div className="font-heading font-bold text-3xl text-slate-900 num">{value}</div>
    {hint && <div className="text-xs text-slate-500">{hint}</div>}
  </div>
);

export default function Dashboard() {
  const { user } = useAuth();
  const [summary, setSummary] = useState(null);
  const [tasks, setTasks] = useState([]);
  const [tickets, setTickets] = useState([]);
  const [events, setEvents] = useState([]);

  useEffect(() => {
    Promise.all([
      api.get("/dashboard/summary").then(r => r.data),
      api.get("/tasks").then(r => r.data),
      api.get("/tickets").then(r => r.data),
      api.get("/events").then(r => r.data),
    ]).then(([s, t, tk, e]) => {
      setSummary(s); setTasks(t); setTickets(tk); setEvents(e);
    });
  }, []);

  const today = new Date().toISOString().slice(0, 10);
  const todayEvents = events.filter(ev => ev.date === today);
  const myTasks = tasks.slice(0, 6);
  const urgentTickets = tickets.filter(t => t.priority === "high" && t.status !== "closed").slice(0, 5);

  return (
    <div className="space-y-6">
      <PageHeader
        title={`Welcome back, ${user?.name?.split(" ")[0] || "there"}`}
        subtitle="Here's what's happening across FigureWeb, FigureCX, and Telecom today."
      />

      {/* KPI Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <KPI label="Total Revenue" value={formatNaira(summary?.total_revenue)} icon={TrendingUp} hint="Across all business units" />
        <KPI label="Open Leads" value={summary?.leads_total ?? "—"} icon={Target} hint={`${summary?.pipeline?.won ?? 0} won this cycle`} />
        <KPI label="Open Tickets" value={summary?.open_tickets_count ?? "—"} icon={TicketIcon} hint={`${summary?.high_priority_tickets ?? 0} high priority`} tone={summary?.high_priority_tickets ? "danger" : "aqua"} />
        <KPI label="Overdue" value={(summary?.overdue_invoices_count ?? 0) + (summary?.overdue_taxes_count ?? 0)} icon={AlertCircle} tone="danger" hint={`${summary?.overdue_invoices_count || 0} invoices · ${summary?.overdue_taxes_count || 0} tax`} />
      </div>

      {/* Pipeline + Revenue by unit */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="kpi-card lg:col-span-2">
          <div className="flex items-center justify-between mb-4">
            <div>
              <div className="font-heading font-semibold text-lg text-slate-900">Leads Pipeline</div>
              <div className="text-xs text-slate-500">Distribution across stages</div>
            </div>
          </div>
          <div className="space-y-3">
            {[
              ["new", "New"], ["contacted", "Contacted"], ["qualified", "Qualified"],
              ["proposal", "Proposal"], ["won", "Won"], ["lost", "Lost"],
            ].map(([key, label]) => {
              const count = summary?.pipeline?.[key] || 0;
              const total = Object.values(summary?.pipeline || {}).reduce((a, b) => a + b, 0) || 1;
              const pct = Math.round((count / total) * 100);
              return (
                <div key={key} className="flex items-center gap-3">
                  <div className="w-24 text-sm text-slate-600">{label}</div>
                  <div className="flex-1 h-2 bg-slate-100 rounded-full overflow-hidden">
                    <div className={`h-full rounded-full ${key === "won" ? "bg-emerald-500" : key === "lost" ? "bg-red-400" : "bg-brand-aqua"}`} style={{ width: `${pct}%` }} />
                  </div>
                  <div className="w-10 text-right text-sm font-medium num">{count}</div>
                </div>
              );
            })}
          </div>
        </div>

        <div className="kpi-card">
          <div className="font-heading font-semibold text-lg text-slate-900 mb-4">Revenue by Unit</div>
          <div className="space-y-4">
            {Object.entries(summary?.revenue_by_unit || {}).map(([unit, amt]) => {
              const total = Object.values(summary?.revenue_by_unit || {}).reduce((a, b) => a + b, 0) || 1;
              const pct = Math.round((amt / total) * 100);
              return (
                <div key={unit}>
                  <div className="flex justify-between text-sm mb-1">
                    <span className="text-slate-700 font-medium">{unit}</span>
                    <span className="num text-slate-900 font-medium">{formatNaira(amt)}</span>
                  </div>
                  <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                    <div className="h-full bg-brand-aqua rounded-full" style={{ width: `${pct}%` }} />
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Today panels */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="kpi-card">
          <div className="font-heading font-semibold text-lg text-slate-900 mb-3">Today's Schedule</div>
          {todayEvents.length === 0 ? (
            <div className="text-sm text-slate-500 py-6 text-center">No events scheduled today.</div>
          ) : (
            <ul className="space-y-2">
              {todayEvents.map(ev => (
                <li key={ev.id} className="flex items-start gap-3 py-2 border-b border-slate-100 last:border-0">
                  <div className="w-1 h-10 rounded-full bg-brand-aqua shrink-0" />
                  <div>
                    <div className="text-sm font-medium text-slate-800">{ev.title}</div>
                    <div className="text-xs text-slate-500">{ev.start_time} – {ev.end_time}</div>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="kpi-card">
          <div className="font-heading font-semibold text-lg text-slate-900 mb-3">My Tasks</div>
          {myTasks.length === 0 ? (
            <div className="text-sm text-slate-500 py-6 text-center">No tasks yet.</div>
          ) : (
            <ul className="space-y-2">
              {myTasks.map(t => (
                <li key={t.id} className="flex items-center justify-between gap-3 py-2 border-b border-slate-100 last:border-0">
                  <div className="text-sm text-slate-700 truncate">{t.title}</div>
                  <Chip variant={statusChip(t.status)}>{t.status.replace("_", " ")}</Chip>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="kpi-card">
          <div className="font-heading font-semibold text-lg text-slate-900 mb-3">Priority Tickets</div>
          {urgentTickets.length === 0 ? (
            <div className="text-sm text-slate-500 py-6 text-center">No high-priority tickets.</div>
          ) : (
            <ul className="space-y-2">
              {urgentTickets.map(t => (
                <li key={t.id} className="py-2 border-b border-slate-100 last:border-0">
                  <div className="flex justify-between items-start gap-2">
                    <div className="text-sm font-medium text-slate-800 truncate">{t.subject}</div>
                    <Chip variant={priorityChip(t.priority)}>{t.priority}</Chip>
                  </div>
                  <div className="text-xs text-slate-500 mt-0.5">{t.contact_name} · {t.business_unit}</div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}
